import { useState } from "react";
import tw from "tailwind-styled-components";
import { useFormik } from "formik";
import moment from "moment";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import {
    InputGroup,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
} from "../Styles/InputStyles";
import axios from "axios";
import Config from "../../Config";
import { useSelector } from "react-redux";
import DropZone from "../Dues/DropZone";
import SelectOptionModal from "../SelectOptionModal";
import SelectMultipleStaffModal from "../SelectMultipleStaffModal";
import CategoryCard from "../CategoryCard";
import { capitalizeFirstLetter, hasPermission } from "../../helpers";
import { FaBed, FaBuilding } from "react-icons/fa";

const AddHousekeepingRecord = ({
    handleClose,
    addCleaningMutate,
    addCleaningLoading,
    taskList = [],
    commonAreaList = [],
}) => {
    const user = useSelector((state) => state.UserReducer.user);
    const showStaff = hasPermission("MANAGE_OTHER_STAFF_HOUSEKEEPING", user);
    // ---------- Modal state ----------
    const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
    const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
    const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
    const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
    const [mediaFiles, setMediaFiles] = useState([]);

    // ---------- Type: 1 = Room, 2 = Common Area ----------
    const [recordType, setRecordType] = useState(1);

    // ---------- Fetched rooms ----------
    const [roomOptions, setRoomOptions] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(false);

    // ---------- Options ----------
    const propertyList =
        useSelector((state) => state.PropertyReducer.property)?.filter(
            (p) => p?.id !== 0,
        ) || [];

    const staffData =
        useSelector((state) => state.StaffListReducer.staffList) || [];

    const propertyOptions = propertyList.map((p) => ({
        value: p.id,
        label: p.name,
    }));

    const staffOptions = (staffData || []).map((s) => ({
        value: s.id ?? s.staffId ?? s.userId,
        label: capitalizeFirstLetter(
            s.name ?? s.staffName ?? s.userName ?? "-",
        ),
        description:
            s.mobile ?? s.staffMobile ?? s.phone
                ? `#${s.mobile ?? s.staffMobile ?? s.phone}`
                : "",
    }));

    // ---------- Common area options (from props) ----------
    const commonAreaOptions = (commonAreaList || []).map((area) => ({
        id: String(area.id),
        name: area.name ?? area.areaName ?? "-",
    }));

    // ---------- Formik ----------
    const formik = useFormik({
        initialValues: {
            property: null,
            rooms: [],
            areas: [],
            staff: null,
            checklist: new Set(taskList.map((t) => t.id)),
            commonAreaChecklist: new Set(), // nothing selected by default
            remarks: "",
            files: [],
        },

        onSubmit: (values) => {
            // ---------- Validation ----------
            if (!values.property) {
                toast.error("Please select a property");
                return;
            }

            if (recordType === 1) {
                // Room validation
                if (!values.rooms || values.rooms.length === 0) {
                    toast.error("Please select at least one room");
                    return;
                }
                if (!values.checklist || values.checklist.size === 0) {
                    toast.error(
                        "Please select at least one checklist item",
                    );
                    return;
                }
            } else {
                // Common area validation
                if (!values.areas || values.areas.length === 0) {
                    toast.error("Please select at least one area");
                    return;
                }
            }

            if (showStaff && !values.staff) {
                toast.error("Please select a staff");
                return;
            }

            if (values.remarks && values.remarks.length > 500) {
                toast.error("Remarks cannot exceed 500 characters");
                return;
            }

            // ---------- Build FormData ----------
            const formData = new FormData();
            formData.append("type", recordType);
            formData.append("propId", values.property.value);
            // formData.append("staffId", values.staff.value);
            formData.append("taskDate", moment().format("YYYY-MM-DD"));
            if (values.staff?.value) {
                formData.append("staffId", values.staff.value);
            }
            if (recordType === 1) {
                // Room type
                formData.append(
                    "roomIds",
                    values.rooms.map((r) => r.id).join(","),
                );

                let mask = 0;
                taskList.forEach((task, index) => {
                    if (values.checklist.has(task.id)) mask |= 1 << index;
                });
                formData.append("taskCompleted", mask);
            } else {
                formData.append(
                    "areaIds",
                    values.areas.map((a) => a.id).join(","),
                );

                // 2) build the mask from a Set of selected ids
                const selectedAreaIds = new Set(values.areas.map((a) => String(a.id)));
                let commonMask = 0;
                commonAreaList.forEach((common, index) => {
                    if (selectedAreaIds.has(String(common.id))) {
                        commonMask |= 1 << index;
                    }
                });
                formData.append("taskCompleted", commonMask);
            }

            if (values.remarks?.trim()) {
                formData.append("remarks", values.remarks.trim());
            }

            mediaFiles.forEach((file) => {
                formData.append("files", file);
            });

            addCleaningMutate(formData);
        },
    });

    // ---------- Fetch rooms ----------
    const fetchRooms = async (propId) => {
        setRoomsLoading(true);
        try {
            const response = await axios.get(
                `${Config.apiUrl}/room/list?propId=${propId}`,
                {
                    headers: {
                        authorization: "Bearer " + user.token,
                        "Content-Type":
                            "application/x-www-form-urlencoded; charset=UTF-8",
                    },
                },
            );

            const rooms = response.data?.rooms ?? [];
            const mapped = rooms.map((room) => {
                const groupLabel =
                    room.propType === Config.PROPERTY_TYPE.PG
                        ? `Floor-${room.flatFloorName ?? "-"}`
                        : `Flat-${room.flatFloorName ?? "-"}`;
                return {
                    id: String(room.id),
                    name: `${groupLabel} Room-${room.roomNum}`,
                };
            });
            setRoomOptions(mapped);
        } catch (error) {
            console.error("Failed to load rooms", error);
            toast.error("Failed to load rooms");
            setRoomOptions([]);
        } finally {
            setRoomsLoading(false);
        }
    };

    // ---------- Handlers ----------
    const handlePropertySelect = (selected) => {
        const option =
            typeof selected === "object" && selected !== null
                ? selected
                : propertyOptions.find((o) => o.value === selected);

        if (!option) {
            setIsPropertyModalOpen(false);
            return;
        }

        if (formik.values.property?.value === option.value) {
            setIsPropertyModalOpen(false);
            return;
        }

        formik.setFieldValue("property", option);
        formik.setFieldValue("rooms", []);
        formik.setFieldValue("areas", []);
        setIsPropertyModalOpen(false);
        fetchRooms(option.value);
    };

    const handleRoomSelect = (selected) => {
        let rooms = [];
        if (Array.isArray(selected) && selected.length > 0) {
            if (typeof selected[0] === "object") {
                rooms = selected;
            } else {
                rooms = roomOptions.filter((r) =>
                    selected.includes(r.id),
                );
            }
        }
        formik.setFieldValue("rooms", rooms);
        setIsRoomModalOpen(false);
    };

    const handleAreaSelect = (selected) => {
        let areas = [];
        if (Array.isArray(selected) && selected.length > 0) {
            if (typeof selected[0] === "object") {
                areas = selected;
            } else {
                areas = commonAreaOptions.filter((a) =>
                    selected.includes(a.id),
                );
            }
        }
        formik.setFieldValue("areas", areas);
        setIsAreaModalOpen(false);
    };

    const handleStaffSelect = (selected) => {
        const option =
            typeof selected === "object" && selected !== null
                ? selected
                : staffOptions.find((o) => o.value === selected);
        formik.setFieldValue("staff", option ?? null);
        setIsStaffModalOpen(false);
    };

    const toggleChecklist = (taskId) => {
        const current = new Set(formik.values.checklist);
        if (current.has(taskId)) current.delete(taskId);
        else current.add(taskId);
        formik.setFieldValue("checklist", current);
    };

    // ---------- Type change handler ----------
    const handleTypeChange = (type) => {
        setRecordType(type);
        // Clear selections that belong to the other type
        if (type === 1) {
            formik.setFieldValue("areas", []);
        } else {
            formik.setFieldValue("rooms", []);
        }
    };

    const handleFileUpload = (files) => {
        setMediaFiles(files);
    };

    const renderSelectedItems = (items = [], emptyLabel = "Select") => {
        if (!items || !items.length) return emptyLabel;
        const maxToShow = 2;
        const names = items.slice(0, maxToShow).map((r) => r.name);
        const remaining = items.length - maxToShow;
        return (
            <div className="flex text-left">
                <span>{names.join(", ")}</span>
                {remaining > 0 && (
                    <span className="ml-1">+{remaining} more</span>
                )}
            </div>
        );
    };

    return (
        <Container>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Add Housekeeping Record
                </h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 flex flex-col justify-between">
                <form onSubmit={formik.handleSubmit}>
                    <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                        <Wrapper>
                            {/* ---------- Property ---------- */}
                            <InputGroup>
                                <Label>Select Property *</Label>
                                <CategoryCard
                                    title="Property"
                                    selectedCategory={
                                        formik.values.property
                                            ? {
                                                label: formik.values
                                                    .property.label,
                                                items: [
                                                    formik.values.property,
                                                ],
                                            }
                                            : null
                                    }
                                    onClick={() =>
                                        setIsPropertyModalOpen(true)
                                    }
                                />
                            </InputGroup>

                            {/* ---------- Type Selection (Room / Common Area) ---------- */}
                            <InputGroup>
                                <Label>Select Type *</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <TypeCard
                                        $active={recordType === 1}
                                        onClick={() => handleTypeChange(1)}
                                        type="button"
                                    >
                                        <span
                                            className={`flex h-10 w-10 items-center justify-center rounded-full ${recordType === 1
                                                ? "bg-[#0078BD] text-white"
                                                : "bg-gray-100 text-gray-500"
                                                }`}
                                        >
                                            <FaBed size={18} />
                                        </span>
                                        <span className="font-semibold text-sm">
                                            Room
                                        </span>
                                        <span className="text-xs opacity-75">
                                            Clean rooms
                                        </span>
                                    </TypeCard>

                                    <TypeCard
                                        $active={recordType === 2}
                                        onClick={() => handleTypeChange(2)}
                                        type="button"
                                    >
                                        <span
                                            className={`flex h-10 w-10 items-center justify-center rounded-full ${recordType === 2
                                                ? "bg-[#0078BD] text-white"
                                                : "bg-gray-100 text-gray-500"
                                                }`}
                                        >
                                            <FaBuilding size={18} />
                                        </span>
                                        <span className="font-semibold text-sm">
                                            Common Area
                                        </span>
                                        <span className="text-xs opacity-75">
                                            Clean common areas
                                        </span>
                                    </TypeCard>
                                </div>
                            </InputGroup>

                            {/* ---------- Room Selection (only when Room type) ---------- */}
                            {recordType === 1 && (
                                <InputGroup>
                                    <Label>Select Room *</Label>
                                    <CategoryCard
                                        title="Room"
                                        selectedCategory={
                                            formik.values.rooms?.length > 0
                                                ? {
                                                    label: renderSelectedItems(
                                                        formik.values.rooms,
                                                        "Select Rooms",
                                                    ),
                                                    items: formik.values.rooms,
                                                }
                                                : null
                                        }
                                        onClick={() => {
                                            if (!formik.values.property) {
                                                toast.error(
                                                    "Please select property first",
                                                );
                                                return;
                                            }
                                            if (roomsLoading) return;
                                            if (roomOptions.length === 0) {
                                                toast.error(
                                                    "No rooms available for this property",
                                                );
                                                return;
                                            }
                                            setIsRoomModalOpen(true);
                                        }}
                                    />
                                </InputGroup>
                            )}

                            {/* ---------- Common Area Selection (only when Common Area type) ---------- */}
                            {recordType === 2 && (
                                <InputGroup>
                                    <Label>Select Areas *</Label>
                                    <CategoryCard
                                        title="Common Area"
                                        selectedCategory={
                                            formik.values.areas?.length > 0
                                                ? {
                                                    label: renderSelectedItems(
                                                        formik.values.areas,
                                                        "Select Areas",
                                                    ),
                                                    items: formik.values.areas,
                                                }
                                                : null
                                        }
                                        onClick={() => {
                                            if (!formik.values.property) {
                                                toast.error(
                                                    "Please select property first",
                                                );
                                                return;
                                            }
                                            if (
                                                commonAreaOptions.length === 0
                                            ) {
                                                toast.error(
                                                    "No common areas available",
                                                );
                                                return;
                                            }
                                            setIsAreaModalOpen(true);
                                        }}
                                    />
                                </InputGroup>
                            )}

                            {/* ---------- Staff ---------- */}
                            {showStaff &&
                                <InputGroup>
                                    <Label>Select Staff *</Label>
                                    <CategoryCard
                                        title="Staff"
                                        selectedCategory={
                                            formik.values.staff
                                                ? {
                                                    label: formik.values.staff
                                                        .label,
                                                    items: [
                                                        formik.values.staff,
                                                    ],
                                                }
                                                : null
                                        }
                                        onClick={() =>
                                            setIsStaffModalOpen(true)
                                        }
                                    />
                                </InputGroup>
                            }

                            {/* ---------- Cleaning Checklist (Room only) ---------- */}
                            {recordType === 1 && (
                                <InputGroup>
                                    <Label>Room Cleaning Checklist *</Label>
                                    <div className="w-full p-4 rounded bg-gray-50 flex flex-col gap-6">
                                        {taskList?.length === 0 ? (
                                            <p className="text-sm text-gray-500">
                                                No checklist items available.
                                            </p>
                                        ) : (
                                            taskList.map((task) => {
                                                const checked =
                                                    formik.values.checklist.has(
                                                        task.id,
                                                    );
                                                return (
                                                    <button
                                                        key={task.id}
                                                        type="button"
                                                        onClick={() =>
                                                            toggleChecklist(
                                                                task.id,
                                                            )
                                                        }
                                                        className="flex items-center gap-3 text-left w-full"
                                                    >
                                                        <span
                                                            className={`w-5 h-5 rounded border flex items-center justify-center text-xs ${checked
                                                                ? "bg-[#0078BD] border-[#0078BD] text-white"
                                                                : "bg-white border-gray-300 text-transparent"
                                                                }`}
                                                        >
                                                            {checked && "✓"}
                                                        </span>
                                                        <span className="text-sm text-gray-700">
                                                            {task.name}
                                                        </span>
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>
                                </InputGroup>
                            )}

                            {/* ---------- Remarks ---------- */}
                            <InputGroup>
                                <Label htmlFor="remarks">Remarks</Label>
                                <FieldWrapper>
                                    <Textarea
                                        id="remarks"
                                        name="remarks"
                                        rows={4}
                                        maxLength={500}
                                        placeholder="Write your remarks here..."
                                        value={formik.values.remarks}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </FieldWrapper>
                                <p className="text-xs text-gray-400 mt-1">
                                    {formik.values.remarks?.length || 0}/500
                                </p>
                            </InputGroup>

                            {/* ---------- Images ---------- */}
                            <InputGroup>
                                <Label>Attach Images</Label>
                                <DropZone
                                    componentFor="expense"
                                    File={mediaFiles}
                                    setFieldValue={(field, value) => {
                                        handleFileUpload(value);
                                    }}
                                    fields={{
                                        name: "files",
                                        value: mediaFiles,
                                        onChange: handleFileUpload,
                                    }}
                                />
                            </InputGroup>
                        </Wrapper>
                    </ScrollableContent>

                    <ActionSection>
                        <SideCancelBtn
                            type="button"
                            onClick={() => {
                                formik.resetForm();
                                setMediaFiles([]);
                                handleClose();
                            }}
                        >
                            Cancel
                        </SideCancelBtn>
                        <SubmitBtn
                            type="submit"
                            disabled={addCleaningLoading}
                        >
                            {addCleaningLoading ? "Submitting..." : "Submit"}
                        </SubmitBtn>
                    </ActionSection>
                </form>

                {/* ---------- Modals ---------- */}
                <SelectOptionModal
                    isOpen={isPropertyModalOpen}
                    onClose={() => setIsPropertyModalOpen(false)}
                    title="Select Property"
                    options={propertyOptions}
                    selectedValue={formik.values.property?.value}
                    onSelect={handlePropertySelect}
                    searchPlaceholder="Search properties..."
                />

                <SelectMultipleStaffModal
                    isOpen={isRoomModalOpen}
                    onClose={() => setIsRoomModalOpen(false)}
                    title="Select Rooms"
                    options={roomOptions}
                    selectedItem={formik.values.rooms}
                    onSelect={handleRoomSelect}
                    searchPlaceholder="Search rooms..."
                    idKey="id"
                    nameKey="name"
                    emptyMessage="No rooms available"
                    noResultsMessage="No rooms found"
                    showClearSelection={true}
                />

                <SelectMultipleStaffModal
                    isOpen={isAreaModalOpen}
                    onClose={() => setIsAreaModalOpen(false)}
                    title="Select Common Areas"
                    options={commonAreaOptions}
                    selectedItem={formik.values.areas}
                    onSelect={handleAreaSelect}
                    searchPlaceholder="Search areas..."
                    idKey="id"
                    nameKey="name"
                    emptyMessage="No areas available"
                    noResultsMessage="No areas found"
                    showClearSelection={true}
                />

                <SelectOptionModal
                    isOpen={isStaffModalOpen}
                    onClose={() => setIsStaffModalOpen(false)}
                    title="Select Staff"
                    options={staffOptions}
                    selectedValue={formik.values.staff?.value}
                    onSelect={handleStaffSelect}
                    searchPlaceholder="Search staff..."
                />
            </div>
        </Container>
    );
};

export default AddHousekeepingRecord;

// ---------------- Styled Components ----------------
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;
const FieldWrapper = tw.div`w-full`;

// ---------- Type Card ----------
const TypeCard = tw.button`
    ${(p) =>
        p.$active
            ? "border-[#0078BD] bg-blue-50 text-[#0078BD] shadow-sm"
            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}
    flex flex-col items-center justify-center gap-1 rounded-lg border p-4 transition-all duration-200 cursor-pointer
`;